import { UploadApi } from "../../constants/Constants";

const REQUEST_TIMEOUT_MS = 20000;

/** Failure of an auth request, with the machine-readable bits the UI switches on. */
export class AuthRequestError extends Error {
  constructor(message, { status = 0, code = "", retryAfter = 0, attemptsLeft } = {}) {
    super(message);
    this.name = "AuthRequestError";
    this.status = status;
    this.code = code; // e.g. OTP_INVALID, OTP_EXPIRED, NETWORK
    this.retryAfter = retryAfter; // seconds
    this.attemptsLeft = attemptsLeft;
  }
}

const post = async (path, body) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response;
  try {
    response = await fetch(`${UploadApi}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (error) {
    throw new AuthRequestError(
      error?.name === "AbortError"
        ? "The server took too long to respond. Please try again."
        : "Can't reach the server. Check your connection and try again.",
      { code: "NETWORK" }
    );
  } finally {
    clearTimeout(timer);
  }

  let data = null;
  try {
    data = await response.json();
  } catch {
    // proxy / gateway error pages are not JSON
  }

  if (!response.ok) {
    throw new AuthRequestError(
      data?.error || data?.message || "Something went wrong. Please try again.",
      {
        status: response.status,
        code: data?.code || "",
        retryAfter: Number(data?.retryAfter) || 0,
        attemptsLeft: data?.attemptsLeft,
      }
    );
  }

  if (!data) {
    throw new AuthRequestError("The server sent an unexpected response.", {
      status: response.status,
      code: "BAD_RESPONSE",
    });
  }

  return data;
};

export const signInRequest = (email, password) =>
  post("/user/sign-in", { email, password });

export const verifyOtpRequest = (challengeId, otp) =>
  post("/user/verify-otp", { challengeId, otp });

export const resendOtpRequest = (challengeId) =>
  post("/user/resend-otp", { challengeId });

/** Codes after which the pending sign-in cannot continue: the user has to start over. */
export const SESSION_ENDING_CODES = new Set([
  "OTP_SESSION_INVALID",
  "OTP_TOO_MANY_ATTEMPTS",
  "OTP_LOCKED",
  "OTP_RATE_LIMITED",
  "OTP_RESEND_LIMIT",
]);

const formatWait = (seconds) => {
  if (seconds < 60) return `${Math.max(1, seconds)} seconds`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
};

/** What to tell the user when a pending sign-in has ended (shown back on the sign-in form). */
export const sessionEndedMessage = (error) => {
  switch (error?.code) {
    case "OTP_TOO_MANY_ATTEMPTS":
      return "Too many incorrect codes. Please sign in again.";
    case "OTP_LOCKED":
    case "OTP_RATE_LIMITED":
      return error.retryAfter
        ? `Too many attempts. Please try again in ${formatWait(error.retryAfter)}.`
        : "Too many attempts. Please try again later.";
    case "OTP_RESEND_LIMIT":
      return "You've used all the code requests for this sign-in. Please sign in again.";
    default:
      return "Your sign-in session expired. Please sign in again.";
  }
};
